import { assertPartialSchema, createAction, useSelector, v } from "../../../lib";
import { mergeUniqueItems, toHydratedSet } from "../../serializable";

export enum GarminConnectDataDigestActionType {
  GARMIN_CONNECT_DIGEST_SET = "GARMIN_CONNECT_DIGEST_SET",
}

export const doGARMIN_CONNECT_DIGEST_SET = createAction(
  GarminConnectDataDigestActionType.GARMIN_CONNECT_DIGEST_SET,
);

interface GarminConnectDataDigestState {
  [projectId: string]: SerializableProjectGarminConnectDataDigest;
}

interface SerializableProjectGarminConnectDataDigest {
  coveredYymmddIndexList: number[];
  participantDataDigestMap: ParticipantGarminConnectHrTimeMap;
}

export interface ProjectGarminConnectDataDigest {
  coveredYymmddIndexSet: Set<number>;
  participantDataDigestMap: ParticipantGarminConnectHrTimeMap;
}

export type ParticipantGarminConnectHrTimeMap = {
  [participantId: string]: GarminConnectDataDigestDateMap;
};
export type GarminConnectDataDigestDateMap = { [yymmdd: string]: GarminConnectDataDigest | null };
export interface GarminConnectDataDigest {
  wearTime: number;
}

export interface ProjectGarminConnectWearTime {
  projectId: string;
  queryYymmddIndexList: number[];
  participantGarminConnectWearTimeMap: ParticipantGarminConnectWearTimeMap;
}

type ParticipantGarminConnectWearTimeMap = {
  [participantId: string]: { [yymmdd: string]: number };
};

interface RootState {
  garminConnectDataDigest: GarminConnectDataDigestState;
}

const projectGarminConnectDataDigestCache = new WeakMap<
  SerializableProjectGarminConnectDataDigest,
  ProjectGarminConnectDataDigest
>();

/* selector */
export const useSelectGarminConnectDataDigest = (projectId: string | undefined) => {
  return useSelector((state: RootState) => {
    if (projectId != undefined) {
      return hydrateProjectGarminConnectDataDigest(state.garminConnectDataDigest[projectId]);
    }
    return undefined;
  });
};

export const useSelectGarminConnectDateDigestCoveredIndexSet = (projectId: string | undefined) => {
  return useSelector((state: RootState) => {
    if (projectId != undefined) {
      return hydrateProjectGarminConnectDataDigest(state.garminConnectDataDigest[projectId])
        ?.coveredYymmddIndexSet;
    }
    return undefined;
  });
};

export const useSelectGarminConnectParticipantDataDigestMap = (projectId: string | undefined) => {
  return useSelector((state: RootState) => {
    if (projectId === undefined) {
      return undefined;
    }
    return state.garminConnectDataDigest[projectId]?.participantDataDigestMap;
  });
};

export const useSelectParticipantGarminConnectDataDigest = (
  projectId: string | undefined,
  participantId: string,
  yymmddIndex: string,
) => {
  return useSelector((state: RootState) => {
    if (projectId === undefined) {
      return undefined;
    }
    return state.garminConnectDataDigest[projectId]?.participantDataDigestMap[participantId]?.[
      yymmddIndex
    ];
  });
};

export const garminConnectDataDigestActionCreators = {
  doGARMIN_CONNECT_DIGEST_SET,
};

export const garminConnectDataDigestDefaultState: GarminConnectDataDigestState = {};

type Action = {
  type: GarminConnectDataDigestActionType.GARMIN_CONNECT_DIGEST_SET;
  payload: {
    projectGarminConnectWearTime: ProjectGarminConnectWearTime;
  };
};

export const garminConnectDataDigestReducer = (
  state = { ...garminConnectDataDigestDefaultState },
  { type, payload }: Action,
): GarminConnectDataDigestState => {
  switch (type) {
    case GarminConnectDataDigestActionType.GARMIN_CONNECT_DIGEST_SET:
      assertPartialSchema({
        payload,
        schema: v.object({
          projectGarminConnectWearTime: v.object({
            projectId: v.string().exist(),
            queryYymmddIndexList: v.array().items(v.number()).required(),
            participantGarminConnectWearTimeMap: v.object().exist(),
          }),
        }),
      });
      return updateProjectGarminConnectWearTimeInState(state, payload.projectGarminConnectWearTime);

    default:
      return state;
  }
};

export const hydrateProjectGarminConnectDataDigest = (
  projectDataDigest: SerializableProjectGarminConnectDataDigest | undefined,
): ProjectGarminConnectDataDigest | undefined => {
  if (!projectDataDigest) {
    return undefined;
  }

  const cachedProjectDataDigest = projectGarminConnectDataDigestCache.get(projectDataDigest);
  if (cachedProjectDataDigest) {
    return cachedProjectDataDigest;
  }

  const hydratedProjectDataDigest: ProjectGarminConnectDataDigest = {
    coveredYymmddIndexSet:
      toHydratedSet(projectDataDigest.coveredYymmddIndexList) ?? new Set<number>(),
    participantDataDigestMap: projectDataDigest.participantDataDigestMap,
  };

  projectGarminConnectDataDigestCache.set(projectDataDigest, hydratedProjectDataDigest);

  return hydratedProjectDataDigest;
};

function updateProjectGarminConnectWearTimeInState(
  state: GarminConnectDataDigestState,
  projectGarminConnectWearTime: ProjectGarminConnectWearTime,
) {
  const { projectId, queryYymmddIndexList, participantGarminConnectWearTimeMap } =
    projectGarminConnectWearTime;

  const existingProjectDigest = state[projectId] ?? {
    coveredYymmddIndexList: [],
    participantDataDigestMap: {},
  };

  const participantDataDigestMap: ParticipantGarminConnectHrTimeMap = {
    ...existingProjectDigest.participantDataDigestMap,
  };

  for (const [participantId, wearTimeMap] of Object.entries(participantGarminConnectWearTimeMap)) {
    const participantDigestMap = { ...(participantDataDigestMap[participantId] ?? {}) };

    for (const [yymmddIndex, wearTime] of Object.entries(wearTimeMap)) {
      participantDigestMap[yymmddIndex] = {
        ...participantDigestMap[yymmddIndex],
        wearTime,
      };
    }

    participantDataDigestMap[participantId] = participantDigestMap;
  }

  return {
    ...state,
    [projectId]: {
      coveredYymmddIndexList:
        mergeUniqueItems(existingProjectDigest.coveredYymmddIndexList, queryYymmddIndexList) ?? [],
      participantDataDigestMap,
    },
  };
}
